import { saveExperiments } from '@/lib/abExperiments';
import { createAnonymousSession } from '@/lib/anonymousSession';
import { COOKIE_KEY } from '@/lib/Constants';
import {
  getCookieForApp,
  getOsType,
  getTokenForApp,
  setCookieForApp,
  setTokenForApp,
} from '@/lib/CookieUtils';
import { decryptAES, encryptAES } from '@/lib/crypto/AES';
import { isAppClient, setLocalToken } from '@/lib/LocalStorage';
import { isSsrGuestDeviceId } from '@/lib/resolveDeviceId';
import { checkRPC } from '@/lib/utils';

let inflight: Promise<string | undefined> | null = null;

function browserDeviceId(): string | undefined {
  const nativeDevice = String(getCookieForApp('hzc_device_id_plain') || '').trim();
  const cookieDevice = decryptAES(getCookieForApp(COOKIE_KEY.DEVICE_ID)).trim();
  if (nativeDevice && !isSsrGuestDeviceId(nativeDevice)) return nativeDevice;
  if (cookieDevice && !isSsrGuestDeviceId(cookieDevice)) return cookieDevice;
  return undefined;
}

/**
 * Public HTML does not Set-Cookie (CDN cache). Browser calls that require a
 * token must wait for one anonymous /user/init instead of racing the header.
 * Native app supplies its own token; do not mint a web session there.
 */
export async function ensureBrowserSession(): Promise<string | undefined> {
  if (typeof window === 'undefined') return undefined;

  const existing = getTokenForApp();
  if (existing) return existing;
  if (isAppClient() || checkRPC('bridge')) return undefined;

  if (!inflight) {
    inflight = (async () => {
      const session = await createAnonymousSession({
        osType: getOsType() || 'web',
        deviceId: browserDeviceId(),
      });
      if (!session?.token) return undefined;

      setTokenForApp(session.token);
      if (session.deviceId) {
        setCookieForApp(COOKIE_KEY.DEVICE_ID, encryptAES(session.deviceId));
      }
      saveExperiments(session.experiments || {});
      setLocalToken(session.token);
      return session.token;
    })().finally(() => {
      inflight = null;
    });
  }

  return inflight;
}
